'use client';

import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useState, useRef, useMemo, useCallback } from "react";
import { MagnifyingGlass, X } from "../../../react-icons.js";
import { Button } from "../Button/Button.js";
import { FormControl } from "../FormControl/FormControl.js";
import { useId } from "../../utils/hooks/useId.js";
import { debounce } from "../../utils/transform/debounce.js";
import { classNames } from "../../utils/styles/classNames.js";
import { getFakeInputBorderClassName } from "./utils/getFakeInputBorderClassName.js";
import { parseProps } from "./utils/parseProps.js";
import { defaultClearButtonLabel } from "./SearchField.data.js";
import { disabledCursor, input, fakeInput, clearButton, inputSpacing, wrapper } from "./SearchField.css.js";
import { defaultProps } from "./SearchField.defaultProps.js";
function SearchFieldComponent(props, ref) {
  const { formControlProps, inputProps } = parseProps(props);
  const {
    dataQa,
    defaultValue,
    id: inputId,
    inputSpacing: inputSpacingProp,
    onChange,
    onClear,
    ...restInputProps
  } = inputProps;
  const { a11yLabel, disabled, ...restFormControlProps } = formControlProps;
  const autoId = useId();
  const isAutoIdNeeded = !inputId || inputId === "";
  const id = isAutoIdNeeded ? autoId : inputId;
  const descriptionId = `chalk-searchfield-description-id-${id}`;
  const inputSpacing$1 = inputSpacingProp || defaultProps.inputSpacing;
  const isDefaultInputSpacing = inputSpacing$1 === defaultProps.inputSpacing;
  const [value, setValue] = useState(defaultValue || "");
  const inputRef = useRef(null);
  const handleChange = useMemo(() => {
    const debouncedOnChange = debounce(onChange, 500);
    return (event) => {
      const v = event.target.value;
      setValue(v);
      debouncedOnChange(v);
    };
  }, [onChange]);
  const handleClear = useCallback(() => {
    var _a;
    setValue("");
    onClear("");
    (_a = inputRef.current) == null ? void 0 : _a.focus();
  }, [onClear]);
  return /* @__PURE__ */ jsx(
    FormControl,
    {
      ...restFormControlProps,
      descriptionId,
      label: {
        ...restFormControlProps.label,
        htmlFor: id
      },
      hiddenLabel: true,
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          className: classNames(
            wrapper,
            inputSpacing[inputSpacing$1],
            disabled && disabledCursor
          ),
          children: [
            /* @__PURE__ */ jsx(
              MagnifyingGlass,
              {
                color: disabled ? "onLight.disabled.muted" : "onLight.icon.secondary",
                size: "md"
              }
            ),
            /* @__PURE__ */ jsx(
              "input",
              {
                ...restInputProps,
                className: classNames(input, disabled && disabledCursor),
                "data-qa": dataQa,
                disabled,
                id,
                ref: (node) => {
                  if (typeof ref === "function") ref(node);
                  else if (ref) ref.current = node;
                  inputRef.current = node;
                },
                type: "search",
                value,
                onChange: handleChange
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: classNames(
                  fakeInput,
                  getFakeInputBorderClassName(formControlProps)
                )
              }
            ),
            value && /* @__PURE__ */ jsx(
              Button,
              {
                className: clearButton,
                emphasis: "low",
                icon: X,
                size: isDefaultInputSpacing ? "sm" : "xs",
                hiddenLabel: true,
                onClick: handleClear,
                children: defaultClearButtonLabel
              }
            )
          ]
        }
      )
    }
  );
}
const SearchField = /* @__PURE__ */ forwardRef(SearchFieldComponent);
export {
  SearchField
};
